/**
 * @module playerManager
 * @private
 */

import { update, resetSubtitlesList } from "../../../utils/externalSubtitles";
import { getOriginalLanguage } from "./trackTransformer";

const trackTypes = {
  AUDIO: "AUDIO",
  TEXT: "TEXT"
};

let sdk = {};
let currentAudioTrack = null;
let currentSubtitleTrack = null;
let subtitles;
let externalSubtitles;
let isExternalSubtitlesLoaded = false;
let currentLang = null;

/**
 * Returns track info for external subtitles
 */
function getTrackInfoExternal() {
  return externalSubtitles
    ? Object.keys(externalSubtitles).map((key, index) => {
        return {
          index,
          language: key,
          type: "TEXT"
        };
      })
    : [];
}

function onTimeChange() {
  const currentSubtitles = subtitles[currentLang];
  update.call(sdk, currentSubtitles);
}

/**
 * Sets xml of subtitles in available languages and subscribes to "timeChages" event in order to updates subtitles
 */
async function setExternalSubtitles() {
  const formattedSubtitles = Object.keys(externalSubtitles).map(key => ({ key, value: externalSubtitles[key] }));
  // FIXME This code is repeated at least in media.js. It should be moved to a decorator or single function instead
  const headers = Object.assign({}, sdk.http.defaults.headers);
  delete sdk.http.defaults.headers["Client-Type"];
  try {
    subtitles = await Promise.all(
      formattedSubtitles.map(async sub => {
        const result = await sdk.http.get(sdk.parseNonSecureMediaContentUrl(sub.value));
        const key = sub.key;
        return {
          [key]: result.data
        };
      })
    );
  } catch (error) {
    console.log(`setExternalSubtitles method failed: ${error.message}`);
    subtitles = [];
  } finally {
    Object.assign(sdk.http.defaults.headers, headers);
    subtitles = Object.assign({}, ...subtitles);
    sdk.playerManager.on("timeChange", onTimeChange);
  }
}

function bindEvents() {
  sdk.playerManager.on("willStop", destroyExternalSubtitles);
  sdk.playerManager.on("play", resetSubtitlesList);
}

function destroyExternalSubtitles() {
  subtitles = null;
  externalSubtitles = null;
  isExternalSubtitlesLoaded = false;
  currentLang = null;
  resetSubtitlesList();
  sdk.playerManager.off("timeChange", onTimeChange);
  sdk.playerManager.off("play", resetSubtitlesList);
  sdk.playerManager.off("willStop", destroyExternalSubtitles);
}

function createSubtitlesInfo(tracks) {
  const tracksSubtitle = [];
  for (let i = 0; i < Object.keys(tracks).length; i++) {
    tracksSubtitle.push({
      index: i,
      type: "TEXT",
      language: getOriginalLanguage(tracks[i].lang),
      lang: tracks[i].lang,
      extra_info: {}
    });
  }
  tracksSubtitle.push({
    index: tracks.length,
    type: "TEXT",
    language: "off",
    lang: "off",
    extra_info: {}
  });
  return tracksSubtitle;
}

function createAudioInfo(tracks) {
  const tracksAudio = [];
  for (let i = 0; i < Object.keys(tracks).length; i++) {
    tracksAudio.push({
      index: i,
      type: "AUDIO",
      language: getOriginalLanguage(tracks[i].lang),
      lang: tracks[i].lang,
      extra_info: {}
    });
  }
  return tracksAudio;
}

export function tracksInitialize(config) {
  sdk = this;
  externalSubtitles = config.externalSubtitles;
}

/**
 * Returns the currently active subtitle track
 * @returns {array<Object>} A list of subtitle tracks with index, type and extra_info properties.
 */
export function getCurrentSubtitleTrack() {
  return currentSubtitleTrack;
}

/**
 * Activates the received subtitle track on the player.
 * @param {Object} track  A track object that at least needs to contain an index property
 */
export function setSubtitleTrack(track) {
  try {
    if (externalSubtitles) {
      currentLang = track.language;
      if (!isExternalSubtitlesLoaded) {
        setExternalSubtitles();
        isExternalSubtitlesLoaded = true;
        bindEvents();
      } else {
        resetSubtitlesList();
      }
    }

    currentSubtitleTrack = track;
  } catch (e) {
    console.log(e);
  }
}

/**
 * Returns a list of audio tracks present on the current video.
 * @returns {array<Object>} A list of audio video tracks with index, type and extra_info properties.
 * @throws Error If the player is in a state that doesn't accept calls to get audio tracks
 */
export function getAvailableAudioTracks(audioTracks) {
  const tracksAudio = createAudioInfo(audioTracks);
  return tracksAudio;
}

/**
 * Returns a list of subtitle tracks present on the current video.
 * @returns {array<Object>} A list of subtitles video tracks with index, type and extra_info properties.
 * @throws Error If the player is in a state that doesn't accept calls to get audio tracks
 */
export function getAvailableSubtitleTracks(subtitleTracks) {
  const tracksSubtitle = createSubtitlesInfo(subtitleTracks);
  return tracksSubtitle;
}

/**
 * Returns the currently active audio track
 * @returns {array<Object>} A list of audio tracks with index, type and extra_info properties.
 */
export function getCurrentAudioTrack() {
  return currentAudioTrack;
}

/**
 * Activates the received audio track on the player.
 * @param {Object} track  A track object that at least needs to contain an index property
 */
export function setAudioTrack(track) {}
